import { createSelector } from 'reselect';
import dayjs from 'dayjs';

const selectHomeState = (state) => state.homeReducer;
const selectWorkers = (state) => state.salonManagementReducer?.workers || [];

const EMPTY_BEST_USERS = [];
const EMPTY_QUEUE = [];
const queueSelectorsByDay = new Map();

export const selectHomeStatistic = createSelector(
  selectHomeState,
  (state) => state.home_statistic,
);

export const selectHomeStatLoading = createSelector(
  selectHomeState,
  (state) => !!state.homeStatLoading,
);

export const selectQueueLoading = createSelector(
  selectHomeState,
  (state) => !!state.queueLoading,
);

export const selectOnboardingData = createSelector(
  selectHomeState,
  (state) => state.onboarding,
);

export const selectBestUsers = createSelector(selectHomeStatistic, (stat) => {
  const list = stat?.bestUser;
  if (!Array.isArray(list) || list.length === 0) return EMPTY_BEST_USERS;
  return list.map(({ nome, cognome, totPrenotazioni, cellulare, email, idutente }) => ({
    clientName: `${nome} ${cognome}`,
    description: null,
    totPrenotazioni,
    phoneNumber: cellulare,
    email,
    id: idutente,
    key: idutente,
  }));
});

export const selectWrappedStats = createSelector(
  selectHomeState,
  (state) => state.wrapped_stats,
);

/** Stable factories — always return the same selector instance. */
export const getHomeStatistic = () => selectHomeStatistic;
export const getHomeStatLoading = () => selectHomeStatLoading;
export const getQueueLoading = () => selectQueueLoading;
export const getOnboardingData = () => selectOnboardingData;
export const getBestUsers = () => selectBestUsers;
export const getWrappedStats = () => selectWrappedStats;

export const getQueue = (date) => {
  const key = dayjs(date).format('YYYY-MM-DD');
  let selector = queueSelectorsByDay.get(key);
  if (!selector) {
    selector = createSelector(selectHomeState, selectWorkers, (home, workers) => {
      const workersList = Array.isArray(workers) ? workers : [];
      const workerNames =
        workersList.length > 0
          ? workersList.reduce((acc, worker) => {
              if (worker?.idpoltrona != null) {
                acc[worker.idpoltrona] = worker.nome_referente;
              }
              return acc;
            }, {})
          : null;

      const dayEntry = (home.queue || []).find((item) =>
        dayjs(item.date, 'DD/MM/YYYY').isSame(dayjs(key, 'YYYY-MM-DD'), 'day'),
      );
      const list = dayEntry?.list;
      if (!Array.isArray(list) || list.length === 0) return EMPTY_QUEUE;

      return list.map((item) => {
        const {
          nome,
          cognome,
          tempo,
          cellulare,
          idUtente,
          idPoltrona,
          nomePoltrona,
          nome_referente,
          idListaAttesa,
        } = item;

        return {
          clientName: `${nome} ${cognome}`,
          description: `${tempo} minuti`,
          phoneNumber: cellulare,
          id: idUtente,
          workerId: idPoltrona,
          workerName:
            nomePoltrona || nome_referente || (workerNames && workerNames[idPoltrona]) || '',
          // null finché il BE non espone idListaAttesa in getQueue → azione "rimuovi" disabilitata
          queueId: idListaAttesa ?? null,
          date: key,
          key: `${idUtente}${idPoltrona}${idListaAttesa ?? ''}`,
        };
      });
    });
    queueSelectorsByDay.set(key, selector);
  }
  return selector;
};
